// src/services/pageActionScanner/PageActionScanner.tsx
// Quét button/input có khai báo data-action-code trên trang hiện tại và đồng bộ vào PageActionInput
// (system-service). Chỉ chạy khi REACT_APP_DEVELOPER_MODE=DEV, chạy lại mỗi khi route thay đổi
// (không quét lúc app khởi động — mỗi lần vào 1 trang mới sẽ tự quét trang đó).

import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { syncPageActionInputs } from "helpers/fakebackend_helper";
import { scanPageActionElements } from "./scanPageActions";

const isDeveloperMode = (): boolean => true; //process.env.REACT_APP_DEVELOPER_MODE === "DEV";

// Debounce sau lần thay đổi DOM cuối cùng trước khi quét lại — bảng load async, popup Thêm/Sửa mở
// ra... đều không đổi URL nên effect theo pathname không tự chạy lại, phải dựa vào MutationObserver.
const SCAN_DEBOUNCE_MS = 500;

const PageActionScanner: React.FC = () => {
  const location = useLocation();
  // Chữ ký (danh sách code đã sắp xếp) của lần đồng bộ gần nhất — tránh gọi API lặp lại khi DOM
  // đổi nhưng tập code tìm được vẫn y hệt (VD: gõ phím trong input, hover đổi class...).
  const lastSyncedSignatureRef = useRef<string>("");

  useEffect(() => {
    if (!isDeveloperMode()) {
      return;
    }

    lastSyncedSignatureRef.current = "";
    let debounceId: number | null = null;

    const runScan = () => {
      const items = scanPageActionElements(location.pathname);
      if (items.length === 0) return;

      const signature = items
        .map((item) => item.code)
        .sort()
        .join("|");
      if (signature === lastSyncedSignatureRef.current) return;
      lastSyncedSignatureRef.current = signature;

      syncPageActionInputs(items).catch((error) => {
        console.error("Đồng bộ PageActionInput thất bại:", error);
      });
    };

    const scheduleScan = () => {
      if (debounceId) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(runScan, SCAN_DEBOUNCE_MS);
    };

    // Quét ngay sau khi trang mount (chờ DOM ban đầu render xong)
    scheduleScan();

    // Theo dõi DOM để bắt các phần tử data-action-code xuất hiện muộn hơn: bảng load xong dữ
    // liệu async, popup Thêm/Sửa được mở, tab con được chuyển...
    const observer = new MutationObserver(() => {
      scheduleScan();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Theo dõi luôn disabled/readonly/aria-disabled — phần tử isDisabledOrReadonly() bị bỏ qua lúc
      // quét, nhưng khi nó chuyển sang enabled (VD: nút Lưu hết disabled sau khi form hợp lệ) thì cần
      // quét lại để đồng bộ, không chỉ dựa vào data-action-code không đổi.
      attributeFilter: [
        "data-action-code",
        "disabled",
        "readonly",
        "aria-disabled",
      ],
    });

    return () => {
      observer.disconnect();
      if (debounceId) window.clearTimeout(debounceId);
    };
  }, [location.pathname]);

  return null;
};

export default PageActionScanner;
